<template>
    <div class="stripe-express-checkout-container" v-if="isExpressCheckoutAvailable">
        <div id="express-checkout-element" :class="{ 'disabled': isDisabled }"></div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, inject, watch, nextTick } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { loadStripe } from '@stripe/stripe-js'
import { usePage } from '@inertiajs/vue3'
import { useSettingsStore } from "@/store/settings";
import { useStorage } from '@vueuse/core';
import { useCheckoutD2CStore } from '@/store/checkoutD2CStore';
import { storeToRefs } from 'pinia';
import { useFetch } from "@/helpers/composables/useFetch";

const props = defineProps({
    amount: {
        type: Number,
        required: true
    },
    discountAmount: {
        type: Number,
        default: 0
    },
    hasRestrictions: {
        type: Boolean,
        default: false
    },
    currency: {
        type: String,
        default: 'USD'
    },
    allowedCountries: {
        type: Array,
        default: () => ['US']
    },
    stripePublishableKey: {
        type: String,
        required: true
    },
    disabled: {
        type: Boolean,
        default: true
    },
    expressCheckout: {
        type: Boolean,
        default: false
    },
    shippingRates: {
        type: Object,
        default: () => ({})
    },
    listId: {
        type: String,
        default: null
    },

})

const settingsStore = useSettingsStore();
const { setNotificationMessage } = settingsStore;
const { recalculateTax, recalculateTaxAwaitable, setTaxPayload } = useCheckoutD2CStore();
let { taxAmount } = storeToRefs(useCheckoutD2CStore());

const isExpressCheckoutAvailable = defineModel('isExpressCheckoutAvailable', {
    type: Boolean,
    default: false
})

const restrictionContext = defineModel('restrictionContext', {
    type: Object,
    default: () => ({
        region: null,
        postalCode: null,
        shippingMethod: null,
    })
})

const emit = defineEmits(['payment-success'])

let route = inject("route");
let trackedViewedExpressCheckout = false;

const jsonFetchHeaders = {
    beforeFetch: (ctx) => {
        ctx.options.headers = {
            ...ctx.options.headers,
            Accept: 'application/json',
        };
        return ctx;
    },
};

const trackViewedExpressCheckout = () => {
    if (!props.expressCheckout || trackedViewedExpressCheckout || typeof route !== 'function') {
        return;
    }
    trackedViewedExpressCheckout = true;
    useFetch(route('express.checkout.viewed'), jsonFetchHeaders).get();
};

const saveEmailForAbandonedCart = (email) => {
    if (!props.expressCheckout || typeof route !== 'function') {
        return;
    }
    const trimmedEmail = String(email ?? '').trim();
    if (!trimmedEmail) {
        return;
    }
    useFetch(route('emailupdate'), jsonFetchHeaders).post({
        email: trimmedEmail,
    }).json();
};

const isProcessing = ref(false)
const stripe = ref(null)
const elements = ref(null)
const expressCheckoutElement = ref(null)
const couponCode = useStorage("coupon", null);
const paymentFailureMessage = ref(null)
const currentTaxPayload = ref(null)

function setSentryUserFromEmail(email) {
    if (!window.sentry || !email) return;
    window.sentry.setUser({ email: String(email).trim() });
}

const form = useForm({
    payment_method: 'stripe_express_checkout',
    stripe_express_checkout_token: '',
    stripe_express_checkout_payment_data: null
})

const shippingInfo = ref(null)
const selectedShippingRate = ref({ id: null, amount: 0, displayName: null})

const subTotalInCents = computed(() => Math.round(props.amount * 100))

const discountAmountInCents = computed(() => Math.round(props.discountAmount * 100))

const taxAmountInCents = computed(() => Math.round(taxAmount.value * 100))

const totalInCents = computed(() => {
    if (!props.expressCheckout) {
        return subTotalInCents.value;
    }
    return Math.max(subTotalInCents.value - discountAmountInCents.value, 0) + selectedShippingCharge.value + taxAmountInCents.value;
})

const total  = computed(() => {
    return totalInCents.value/100;
})

const isDisabled = computed(() => {
    return props.disabled || isProcessing.value
})

const getAddressCountry = (address = {}) => {
    const country = address.country
    return typeof country === 'string' ? country.toUpperCase() : null
}

const allowedCountries = computed(() => {
    return (Array.isArray(props.allowedCountries) ? props.allowedCountries : [])
        .map(country => String(country).toUpperCase())
})

const isAllowedShippingCountry = (address = {}) => {
    const country = getAddressCountry(address)
    return !country || allowedCountries.value.includes(country)
}

const showUnsupportedCountryMessage = (message) => {
    setNotificationMessage({
        message: 'Stripe Express Checkout is only available for orders shipping to the United States.',
        type: 'error'
    })
}

const displayNameFor = (id) => {
    switch (id) {
        case 'standard':
            return 'Standard'
        case 'twoday':
            return 'Two Day'
        case 'threeday':
            return 'Three Day'
        default:
            return String(id)
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (c) => c.toUpperCase())
    }
}

const convertRatesToStripeFormat = (rates, shouldFilterExpedited = false, cartTotal = 0) => {
    if (!rates || typeof rates !== 'object') {
        return []
    }

    return Object.entries(rates)
        .filter(([key]) => key !== 'freeshippingmsg')
        .filter(([, value]) => value !== null && value !== undefined && value !== '' && !Number.isNaN(parseFloat(String(value))))
        // Filter out expedited shipping if cart total exceeds threshold
        .filter(([key]) => {
            if (!shouldFilterExpedited || cartTotal <= usePage().props.d2c_expedited_shipping_max_cart_total) {
                return true;
            }
            return key !== 'twoday' && key !== 'threeday';
        })
        .map(([key, value]) => ({
            id: key,
            displayName: displayNameFor(key),
            amount: Math.round(parseFloat(String(value)) * 100), // Stripe expects minor units (cents)
        }))
}

const shippingOptions = computed(() => {
    return convertRatesToStripeFormat(props.shippingRates, props.expressCheckout, total.value)
})

const selectedShippingCharge = computed(() => {
    return selectedShippingRate.value.amount;
})

const stripeLineItems = computed(() => {
    if (!props.expressCheckout) {
        return [];
    }
    return [
        { name: 'Subtotal', amount: subTotalInCents.value },
        ...(discountAmountInCents.value > 0
            ? [{ name: 'Discount', amount: -discountAmountInCents.value }]
            : []),
        { name: 'Shipping', amount: selectedShippingCharge.value },
        { name: 'Tax', amount: taxAmountInCents.value },
    ];
})

const syncRestrictionContext = (partial = {}) => {
    restrictionContext.value = {
        ...(restrictionContext.value ?? { region: null, postalCode: null, shippingMethod: null }),
        shippingMethod: selectedShippingRate.value.id ?? null,
        ...partial,
    }
}

onMounted(async () => {
    await initializeStripe()
})

watch(() => [props.amount, props.discountAmount], async ([newAmount, newDiscount], [oldAmount, oldDiscount]) => {
    if (newAmount === oldAmount && newDiscount === oldDiscount) {
        return
    }

    if (stripe.value && elements.value && expressCheckoutElement.value) {
        await updateExpressCheckout()
    }
}, { immediate: false })

const initializeStripe = async () => {
    try {
        stripe.value = await loadStripe(props.stripePublishableKey)
        
        if (!stripe.value) {
            throw new Error('Failed to load Stripe')
        }

        const options = {
            mode: 'payment',
            amount: totalInCents.value,
            currency: props.currency,
            captureMethod: 'manual'
        };
        
        elements.value = stripe.value.elements(options)
        
        createExpressCheckoutElement()

    } catch (error) {
        isExpressCheckoutAvailable.value = false
    }
}

const updateExpressCheckout = async () => {
    try {
        const options = {
            mode: 'payment',
            amount: totalInCents.value,
            currency: props.currency,
            captureMethod: 'manual'
        };

        elements.value.update(options)
        
    } catch (error) {
        await reinitializeExpressCheckout()
    }
}

const destroyExpressCheckoutElement = () => {
    if (expressCheckoutElement.value) {
        try {
            expressCheckoutElement.value.unmount()
            expressCheckoutElement.value = null
        } catch (error) {}
    }
}

const reinitializeExpressCheckout = async () => {
    try {
        destroyExpressCheckoutElement()
        
        const container = document.getElementById('express-checkout-element')
        if (container) {
            container.innerHTML = ''
        }
        
        await nextTick()
        
        const options = {
            mode: 'payment',
            amount: totalInCents.value,
            currency: props.currency,
            captureMethod: 'manual'
        };
        
        elements.value = stripe.value.elements(options)
        
        createExpressCheckoutElement()
        
    } catch (error) {
        console.log('stripe express build error', error);
    }
}

const createExpressCheckoutElement = () => {
    try {
        const expressCheckoutOptions = {
            buttonType: {
                applePay: 'buy',
                googlePay: 'buy'
            },
            buttonTheme: {
                applePay: 'black',
                googlePay: 'black'
            },
            paymentMethods: {
                googlePay: 'always',
                applePay: 'auto'
            },
            paymentMethodOrder: ['applePay', 'googlePay'] 
        }

        if (props.expressCheckout) {
            expressCheckoutOptions.lineItems = stripeLineItems.value
            expressCheckoutOptions.billingAddressRequired = true
            expressCheckoutOptions.shippingAddressRequired = true
            expressCheckoutOptions.emailRequired = true
            expressCheckoutOptions.phoneNumberRequired = true
            expressCheckoutOptions.allowedShippingCountries = allowedCountries.value
            expressCheckoutOptions.shippingRates = shippingOptions.value
        }

        const expressCheckout = elements.value.create('expressCheckout', expressCheckoutOptions)

        expressCheckout.mount('#express-checkout-element')

        if (props.expressCheckout) {
            expressCheckout.on('click', (clickEvent) => {
                trackViewedExpressCheckout();
                const accountEmail = settingsStore.email;
                if (accountEmail) {
                    setSentryUserFromEmail(accountEmail);
                    saveEmailForAbandonedCart(accountEmail);
                }
                clickEvent.resolve({
                    lineItems: stripeLineItems.value,
                    shippingRates: shippingOptions.value,
                });
            });
        }

        expressCheckout.on('ready', async ({ availablePaymentMethods }) => {
            const hasAnyActivePaymentMethod = Object.values(availablePaymentMethods ?? {}).some(v => v === true); 
            if (availablePaymentMethods && hasAnyActivePaymentMethod) {
                isExpressCheckoutAvailable.value = true
            } else {
                isExpressCheckoutAvailable.value = false
            }
        })

        expressCheckout.on('cancel', function() {
            const messageStr = paymentFailureMessage.value ? `${paymentFailureMessage.value} ` : 'Payment failed. Please try again or use a different payment method.'
            setNotificationMessage({
                message: messageStr,
                type: 'error'
            })
        });

        expressCheckout.on('confirm', async (event) => {
            try {
                isProcessing.value = true
                const walletEmail = event.billingDetails?.email
                if (walletEmail) {
                    setSentryUserFromEmail(walletEmail);
                    saveEmailForAbandonedCart(walletEmail);
                }

                const shippingRate = event.shippingRate || null;
                const shippingAddress = event.shippingAddress || {}
                const addressOnly = shippingAddress?.address ? shippingAddress.address : shippingAddress
                syncRestrictionContext({
                    region: addressOnly?.state ?? null,
                    postalCode: addressOnly?.postal_code || addressOnly?.postalCode || null,
                })

                if (props.expressCheckout && !isAllowedShippingCountry(shippingAddress.address)) {
                    showUnsupportedCountryMessage()
                    event.paymentFailed({
                        reason: 'address_unserviceable',
                        message: 'Stripe Express Checkout is only available for orders shipping to the United States.'
                    });
                    return;
                }

                if (props.expressCheckout && props.hasRestrictions) {
                    paymentFailureMessage.value =
                        'Some items in your cart are under a restriction. Please remove restricted items to place this order.'
                    setNotificationMessage({
                        message: paymentFailureMessage.value,
                        type: 'error'
                    })
                    event.paymentFailed({
                        reason: 'address_unserviceable',
                        message: paymentFailureMessage.value
                    });
                    return;
                }
                
                const {client_secret: clientSecret} = await createPaymentIntent(shippingAddress, shippingRate);

                const { error, paymentIntent: confirmedPaymentIntent } = await stripe.value.confirmPayment({
                    elements: elements.value,
                    clientSecret,
                    confirmParams: {
                        return_url: route('shop.show'),
                    },
                    redirect: "if_required"
                })

                if (error) {
                    throw error
                }

                form.stripe_express_checkout_payment_data = {
                    paymentIntentId: confirmedPaymentIntent.id,
                }

                const paymentData = {
                    payment: {
                        paymentIntentId: confirmedPaymentIntent.id
                    },
                    amount: props.amount,
                    currency: props.currency
                }

                paymentFailureMessage.value = null
                emit('payment-success', paymentData)

            } catch (error) {
                paymentFailureMessage.value = null
                setNotificationMessage({
                    message: `Payment failed. Please try again or use a different payment method.`,
                    type: 'error'
                })
            } finally {
                isProcessing.value = false
            }
        })


        expressCheckout.on('shippingratechange', async (event) => {
            var resolve = event.resolve;
            const { shippingRate } = event

            if (shippingRate) {
                selectedShippingRate.value.id = shippingRate.id
                selectedShippingRate.value.amount = shippingRate.amount || 0
                selectedShippingRate.value.displayName = shippingRate.displayName || shippingRate.display_name || null
            }
            syncRestrictionContext()
            
            if (currentTaxPayload.value) {
                currentTaxPayload.value.shipping_option = {
                    cost: selectedShippingRate.value.amount / 100 || 0
                };
                setTaxPayload(currentTaxPayload.value);
                await recalculateTaxAwaitable();
                await nextTick();
            }
            
            elements.value.update({
                amount: totalInCents.value
            });

            resolve({
                lineItems: stripeLineItems.value
            });
    
        })

        if (props.expressCheckout) {
            expressCheckout.on('shippingaddresschange', async (event) => {
                var resolve = event.resolve;
                try {
                    const address = event.address;
                    syncRestrictionContext({
                        region: address?.state ?? null,
                        postalCode: address?.postal_code || address?.postalCode || null,
                    })
                    if (!isAllowedShippingCountry(address)) {
                        showUnsupportedCountryMessage()
                        event.reject();
                        return;
                    }
                    const state = address.state;

                    if (!state) {
                        event.reject();
                        return;
                    }
                    const response = await fetch(route('update.shipping'), {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json',
                            'X-CSRF-TOKEN': usePage().props.csrf_token || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                        },
                        body: JSON.stringify({
                            state: state,
                            used_coupon: couponCode.value
                        })
                    });

                    if (!response.ok) {
                        throw new Error('Failed to fetch shipping rates');
                    }

                    const data = await response.json();     
                    const stripeShippingRates = convertRatesToStripeFormat(data, props.expressCheckout, total.value);

                    const standardRate = stripeShippingRates.find(rate => rate.id === 'standard')
                    if (standardRate) {
                        selectedShippingRate.value.id = standardRate.id
                        selectedShippingRate.value.amount = standardRate.amount || 0
                        selectedShippingRate.value.displayName = standardRate.displayName || null
                    }
                    
                    const taxPayloadForm = {
                        shipping_zip: address.postal_code || address.postalCode || null,
                        list_id: props.listId,
                        shipping_option: {
                            cost: selectedShippingRate.value.amount / 100 || 0
                        },
                        shipping_address: address.line1 || address.line_1 || null,
                        shipping_address2: address.line2 || address.line_2 || null,
                        shipping_city: address.city || null,
                        shipping_state: address.state || null,
                        used_coupon: couponCode.value || null,
                    };
                    
                    currentTaxPayload.value = taxPayloadForm;
                    setTaxPayload(taxPayloadForm);     
                    await recalculateTaxAwaitable();
                    await nextTick();

                    if (props.hasRestrictions) {
                        paymentFailureMessage.value =
                            'Some items in your cart are under a restriction. Please remove restricted items to place this order.'
                        setNotificationMessage({
                            message: paymentFailureMessage.value,
                            type: 'error'
                        })
                        event.reject();
                        return;
                    }
                    
                    var payload = {
                        shippingRates: stripeShippingRates,
                        lineItems: stripeLineItems.value
                    };

                    elements.value.update({
                        amount: totalInCents.value
                    });

                    resolve(payload);

                } catch (error) {
                    event.reject();
                }
            });
        }

        expressCheckoutElement.value = expressCheckout
    } catch (error) {
        console.log('stripe express build error', error);
    }
}

const createPaymentIntent = async (shippingAddress, shippingRate = null) => {
    try {
        const requestBody = {
            amount: total.value,
            currency: props.currency
        };

        if (props.expressCheckout) {
            if (shippingAddress) {
                requestBody.shipping_details = shippingAddress;
            }
            if (shippingRate) {
                requestBody.shipping_rate = {
                    id: shippingRate.id,
                    amount: shippingRate.amount
                };
            }
            if (taxAmount.value) {
                requestBody.tax_amount = taxAmountInCents.value;
            }
        }
        
        const response = await fetch(route('endpoint.stripe-express-checkout.create-payment-intent'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': usePage().props.csrf_token
            },
            body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
            throw new Error('Failed to create payment intent');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw error;
    }
}

onUnmounted(() => {
    destroyExpressCheckoutElement()
})

defineExpose({
    form,
    isProcessing,
    shippingInfo
})
</script>

<style scoped>
.stripe-express-checkout-container {
    width: 100%;
}

#express-checkout-element {
    width: 100%;
}

/* Custom styling for the Express Checkout Element */
#express-checkout-element .StripeExpressCheckoutElement {
    border-radius: 8px;
}

#express-checkout-element .StripeExpressCheckoutElement button {
    border-radius: 8px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Disabled state styling */
.stripe-express-checkout-container {
    position: relative;
}

#express-checkout-element.disabled {
    opacity: 0.8;
    pointer-events: none;
}

</style>
